import type { Tool } from './types';

export const lagnaCalculator: Tool = {
  slug: 'lagna-calculator',
  metaTitle: 'Lagna Calculator — Find Your Ascendant (Rising Sign) in Vedic Astrology',
  metaDescription:
    'Free Lagna (ascendant) calculator. Find your Vedic rising sign, lagna lord and degree from your date, time and place of birth — with an AI explanation of what your lagna means.',
  keywords:
    'lagna calculator, ascendant calculator, rising sign calculator vedic, lagna by date of birth, what is my lagna, vedic ascendant, lagna lord, udaya lagna',
  eyebrow: 'Ascendant',
  h1: 'Lagna Calculator (Vedic Ascendant / Rising Sign)',
  appName: 'KundaliAI Lagna Calculator',
  calculator: 'lagna',
  intro:
    'Your lagna — the sidereal sign rising on the eastern horizon when you were born — is the anchor of your entire kundali. KundaliAI computes it to the degree from your date, time and place of birth, identifies your lagna lord and its placement, and explains what your ascendant means for personality, health and the house layout of your chart. Free to start.',
  ctaLabel: 'Find my Lagna free',
  ctaHref: '/chat',
  sections: [
    {
      heading: 'What is the lagna?',
      body: 'The **lagna (udaya lagna)** is the zodiac sign — and exact degree — rising on the eastern horizon at your birth moment and birthplace. It becomes the **1st house** of your kundali, so every other house is counted from it: the lagna decides which planets rule your career, marriage or wealth houses. In Vedic astrology the lagna, not the Sun sign, is the primary lens for reading a chart, which is why Vedic readings often differ from Western horoscope apps.',
    },
    {
      heading: 'How the calculator works',
      body: 'The ascendant moves through the entire zodiac **every 24 hours** — a new sign rises roughly every two hours, and faster or slower depending on latitude. KundaliAI computes the rising degree from your exact birth time and coordinates, applies the **Lahiri ayanamsa** for the sidereal sign, and derives the timezone from your birthplace so historical clock changes don\'t skew the result. Because a two-hour window spans a whole sign, an accurate birth time matters more for the lagna than for anything else in your chart.',
    },
    {
      heading: 'Your lagna lord and what it means',
      body: 'Each lagna is ruled by a planet — Aries by Mars, Taurus by Venus, and so on. This **lagna lord** is the single most important planet in your chart: its sign, house and strength color your vitality, self-expression and life direction. KundaliAI shows where your lagna lord sits and what that placement classically indicates, and the AI astrologer can take it further — yogas involving the lagna, your D9 (navamsa) lagna, and how current dashas interact with it.',
    },
  ],
  faq: [
    {
      question: 'How do I find my lagna by date of birth?',
      answer:
        'You need date, exact time and place of birth. The calculator computes which sidereal sign was rising at that moment from your birthplace\'s coordinates and returns your lagna sign, its exact degree and your lagna lord.',
    },
    {
      question: 'Is lagna the same as my Sun sign or Moon sign?',
      answer:
        'No. The Sun sign is where the Sun was, the Moon sign (rashi) is where the Moon was, and the lagna is the sign rising on the eastern horizon. Vedic astrology reads the chart primarily from the lagna, using the Moon sign as a secondary reference.',
    },
    {
      question: 'What if I don\'t know my exact birth time?',
      answer:
        'The lagna changes roughly every two hours, so an unknown birth time makes it uncertain. If you know an approximate window, KundaliAI can show whether the lagna changes within it; for a precise chart, check your birth record for the recorded time.',
    },
    {
      question: 'Why is my Vedic ascendant different from Western astrology sites?',
      answer:
        'Western sites use the tropical zodiac; Vedic astrology uses the sidereal zodiac, which currently differs by about 24 degrees (the ayanamsa). Your rising degree is the same sky position — the sidereal label often shifts one sign back from the tropical one.',
    },
  ],
  related: [
    { label: 'All 12 ascendants — lords, yogakarakas, house tables', href: '/lagna' },
    { label: 'Free Kundli by date of birth', href: '/free-kundli' },
    { label: 'Nakshatra calculator (birth star)', href: '/nakshatra-calculator' },
    { label: 'Dasha calculator (Vimshottari)', href: '/dasha-calculator' },
  ],
};
